import { serverTimestamp, type FieldValue } from 'firebase/firestore'
import {
  typedCollection,
  typedDoc,
  getDocument,
  getCollection,
  updateDocument,
  deleteDocument,
  addDoc,
  orderBy,
  where,
  deleteField,
} from '@/lib/firebase/firestore'
import type { FilmDocument } from '@/types'
import type { FilmFormValues } from '../types'

function toSlug(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

const filmsCol = () => typedCollection<FilmDocument>('films')

type FilmCreatePayload = Omit<FilmDocument, 'id' | 'createdAt' | 'updatedAt'> & {
  createdAt: FieldValue
  updatedAt: FieldValue
}

export const adminFilmsService = {
  async createFilm(data: FilmFormValues, thumbnailUrl?: string, videoUrl?: string): Promise<string> {
    const slug = toSlug(data.title)
    const isEpisode = data.type === 'episode'
    const payload: FilmCreatePayload = {
      type: data.type,
      title: data.title,
      slug,
      ...(isEpisode ? {
        seasonId: data.seasonId,
        seasonNumber: data.seasonNumber,
        episodeNumber: data.episodeNumber,
      } : {}),
      description: data.description,
      ...(data.youtubeId ? { youtubeId: data.youtubeId } : {}),
      ...(videoUrl ? { videoUrl } : {}),
      ...(thumbnailUrl ? { thumbnailUrl } : {}),
      isPublished: data.isPublished,
      order: data.order,
      choreographers: data.choreographers,
      cast: data.cast,
      crew: data.crew,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    const ref = await addDoc(filmsCol(), payload as unknown as FilmDocument)
    return ref.id
  },

  async updateFilm(id: string, data: Partial<FilmFormValues>, thumbnailUrl?: string, videoUrl?: string): Promise<void> {
    const ref = typedDoc<FilmDocument>('films', id)
    const slug = data.title !== undefined ? { slug: toSlug(data.title) } : {}
    const isEpisode = data.type === 'episode'
    // When switching to project type, remove episode-specific fields from Firestore
    const episodeFieldOverrides = isEpisode
      ? {}
      : { seasonId: deleteField(), seasonNumber: deleteField(), episodeNumber: deleteField() }
    await updateDocument(ref, {
      ...data,
      ...slug,
      ...episodeFieldOverrides,
      ...(thumbnailUrl !== undefined ? { thumbnailUrl } : {}),
      ...(videoUrl !== undefined ? { videoUrl } : {}),
      updatedAt: serverTimestamp(),
    } as unknown as FilmDocument)
  },

  async getFilmById(id: string): Promise<(FilmDocument & { id: string }) | null> {
    const ref = typedDoc<FilmDocument>('films', id)
    const data = await getDocument(ref)
    return data ? { ...data, id } : null
  },

  async deleteFilm(id: string): Promise<void> {
    const ref = typedDoc<FilmDocument>('films', id)
    await deleteDocument(ref)
  },

  // Returns only season episodes (type === 'episode', with fallback for legacy docs without type)
  async getAllFilms(): Promise<Array<FilmDocument & { id: string }>> {
    const all = await getCollection(
      filmsCol(),
      orderBy('seasonNumber', 'asc'),
      orderBy('order', 'asc')
    )
    return all.filter(f => (f.type ?? 'episode') === 'episode')
  },

  async getFilmsBySeason(seasonNumber: number): Promise<Array<FilmDocument & { id: string }>> {
    const all = await getCollection(
      filmsCol(),
      where('seasonNumber', '==', seasonNumber),
      orderBy('order', 'asc')
    )
    return all.filter(f => (f.type ?? 'episode') === 'episode')
  },

  async getAllProjects(): Promise<Array<FilmDocument & { id: string }>> {
    return getCollection(
      filmsCol(),
      where('type', '==', 'project'),
      orderBy('order', 'asc')
    )
  },
}
